/**
 * Route → breadcrumb registry for WorkflowDesk dashboard.
 * Each entry: path prefix → crumb builders.
 */

export const SIDEBAR_GROUPS = [
  {
    id: 'work',
    label: 'Work',
    moduleKeys: ['overview', 'tickets', 'my_teams', 'my_requests'],
  },
  {
    id: 'operations',
    label: 'Operations',
    moduleKeys: ['workforce', 'whatsapp_tasks', 'audit_log'],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    moduleKeys: ['knowledge', 'assets', 'payroll', 'product_management', 'service_catalog'],
  },
  {
    id: 'tools',
    label: 'Tools',
    moduleKeys: ['supporting_tools', 'supporting_tools_live'],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    moduleKeys: ['sla', 'form_builder', 'data_sources', 'automation'],
  },
  {
    id: 'admin',
    label: 'Admin',
    moduleKeys: ['users', 'admin_lookups', 'admin_settings', 'module_access', 'access_control', 'announcements', 'test_executions'],
  },
  {
    id: 'account',
    label: 'Account',
    moduleKeys: ['profile'],
  },
  {
    id: 'support',
    label: 'Support',
    moduleKeys: ['help'],
  },
];

const TICKET_SEGMENTS = {
  new: 'New',
  list: 'All Tickets',
  pipeline: 'Pipeline',
  'my-tickets': 'My Tickets',
  'my-tasks': 'My Tasks',
  'manager-queue': 'Queue',
  reports: 'Reports',
  rca: 'Root Cause Analysis',
  member: 'Members',
  admin: 'Admin',
  transfer: 'Transfer',
  'update-status': 'Update Status',
  closure: 'Closure',
  reopen: 'Reopen / Close',
};

/**
 * Build breadcrumb trail for a pathname under /dashboard.
 * Returns [{ label, to }] — last item may have to=null (current).
 */
export function buildBreadcrumbs(pathname, opts = {}) {
  const path = (pathname || '').replace(/\/+$/, '') || '/dashboard';
  const crumbs = [{ label: 'Overview', to: '/dashboard' }];

  if (path === '/dashboard') {
    return [{ label: 'Overview', to: null }];
  }

  const parts = path.replace(/^\/dashboard\/?/, '').split('/').filter(Boolean);
  if (!parts.length) return crumbs;

  const head = parts[0];

  // Tickets
  if (head === 'tickets') {
    crumbs.push({ label: 'Tickets', to: '/dashboard/tickets' });
    const sub = parts[1];
    if (!sub) return crumbs;
    if (sub === 'reports' && parts[2] === 'member') {
      crumbs.push({ label: 'Reports', to: '/dashboard/tickets/reports' });
      crumbs.push({ label: 'Members', to: null });
      return crumbs;
    }
    if (sub === 'list' && parts[2]) {
      crumbs.push({ label: 'All Tickets', to: '/dashboard/tickets/list' });
      crumbs.push({ label: opts.ticketNo || parts[2], to: null });
      return crumbs;
    }
    const label = TICKET_SEGMENTS[sub] || sub;
    crumbs.push({ label, to: null });
    return crumbs;
  }

  if (head === 'my-teams') {
    crumbs.push({ label: 'My Teams', to: null });
    return crumbs;
  }

  if (head === 'my-requests') {
    crumbs.push({ label: 'My Requests', to: null });
    return crumbs;
  }

  if (head === 'workforce') {
    crumbs.push({ label: 'Work Intelligence', to: null });
    return crumbs;
  }

  if (head === 'whatsapp-tasks') {
    crumbs.push({ label: 'WhatsApp', to: '/dashboard/whatsapp-tasks' });
    if (parts[1] === 'inbox') {
      crumbs.push({ label: 'Inbox', to: null });
    } else if (parts[1] === 'tasks' && parts[2]) {
      crumbs.push({ label: 'Inbox', to: '/dashboard/whatsapp-tasks/inbox' });
      crumbs.push({ label: parts[2], to: null });
    } else if (parts[1]) {
      crumbs.push({ label: parts[1], to: null });
    } else {
      crumbs.push({ label: 'Overview', to: null });
    }
    return crumbs;
  }

  if (head === 'supporting-tools') {
    crumbs.push({ label: 'Supporting Tools', to: '/dashboard/supporting-tools' });
    if (parts[1] === 'connect') {
      crumbs.push({ label: 'Connect', to: null });
    } else if (parts[1] === 'registry') {
      crumbs.push({ label: 'Registry', to: null });
    } else if (parts[1] === 'settings') {
      crumbs.push({ label: 'Settings', to: null });
    } else if (parts[1] === 'clients' && parts[2]) {
      crumbs.push({ label: 'Registry', to: '/dashboard/supporting-tools/registry' });
      crumbs.push({ label: opts.clientName || 'Client', to: null });
      if (opts.toolName) crumbs.push({ label: opts.toolName, to: null });
    } else {
      crumbs[crumbs.length - 1].to = null;
    }
    return crumbs;
  }

  if (head === 'supporting-tools-live') {
    crumbs.push({ label: 'Supporting Tools Live', to: '/dashboard/supporting-tools-live' });
    if (parts[1] === 'connect') {
      crumbs.push({ label: 'Connect', to: null });
    } else if (parts[1] === 'registry') {
      crumbs.push({ label: 'Registry', to: null });
    } else if (parts[1] === 'clients' && parts[2]) {
      crumbs.push({ label: 'Registry', to: '/dashboard/supporting-tools-live/registry' });
      crumbs.push({ label: opts.clientName || 'Client', to: null });
      if (opts.toolName) crumbs.push({ label: opts.toolName, to: null });
    } else {
      crumbs[crumbs.length - 1].to = null;
    }
    return crumbs;
  }

  if (head === 'knowledge') {
    crumbs.push({ label: 'Knowledge Base', to: null });
    return crumbs;
  }
  if (head === 'help') {
    crumbs.push({ label: 'Help', to: null });
    return crumbs;
  }
  if (head === 'service-catalog') {
    crumbs.push({ label: 'Service Catalog', to: null });
    return crumbs;
  }
  if (head === 'payroll') {
    crumbs.push({ label: 'Accounts', to: '/dashboard/payroll' });
    if (parts[1] === 'clients') crumbs.push({ label: 'Client economics', to: null });
    else crumbs.push({ label: 'Payroll / CTC', to: null });
    return crumbs;
  }
  if (head === 'assets') {
    if (parts[1] === 'companies') {
      crumbs.push({ label: 'Assets', to: '/dashboard/assets' });
      if (parts[2]) {
        crumbs.push({ label: 'Companies', to: '/dashboard/assets/companies' });
        crumbs.push({ label: opts.companyName || 'Company', to: null });
      } else {
        crumbs.push({ label: 'Companies', to: null });
      }
      return crumbs;
    }
    if (parts[1] === 'teams') {
      crumbs.push({ label: 'Assets', to: '/dashboard/assets' });
      if (parts[2] && parts[3] === 'members' && parts[4]) {
        crumbs.push({ label: 'Companies', to: '/dashboard/assets/companies' });
        crumbs.push({ label: opts.teamName || 'Team', to: `/dashboard/assets/teams/${parts[2]}` });
        crumbs.push({ label: opts.memberName || 'Member', to: null });
      } else if (parts[2] && parts[3] === 'clients' && parts[4]) {
        crumbs.push({ label: 'Companies', to: '/dashboard/assets/companies' });
        crumbs.push({ label: opts.teamName || 'Team', to: `/dashboard/assets/teams/${parts[2]}` });
        crumbs.push({ label: opts.clientName || 'Client', to: null });
      } else if (parts[2]) {
        crumbs.push({ label: 'Companies', to: '/dashboard/assets/companies' });
        crumbs.push({ label: opts.teamName || 'Team', to: null });
      } else {
        crumbs.push({ label: 'Companies', to: null });
      }
      return crumbs;
    }
    if (parts[1] === 'allocation-report') {
      crumbs.push({ label: 'Assets', to: '/dashboard/assets' });
      crumbs.push({ label: 'Allocation Report', to: null });
      return crumbs;
    }
    if (parts[1] === 'economics') {
      crumbs.push({ label: 'Assets', to: '/dashboard/assets' });
      if (parts[2] === 'products') {
        crumbs.push({ label: 'Economics', to: '/dashboard/assets/economics/products' });
        if (parts[3]) {
          crumbs.push({ label: 'By product', to: '/dashboard/assets/economics/products' });
          crumbs.push({ label: opts.productName || 'Product', to: null });
        } else {
          crumbs.push({ label: 'By product', to: null });
        }
        return crumbs;
      }
      if (parts[2] === 'clients') {
        crumbs.push({ label: 'Economics', to: '/dashboard/assets/economics/clients' });
        if (parts[3]) {
          crumbs.push({ label: 'By client', to: '/dashboard/assets/economics/clients' });
          crumbs.push({ label: opts.clientName || 'Client', to: null });
        } else {
          crumbs.push({ label: 'By client', to: null });
        }
        return crumbs;
      }
      if (parts[2] === 'spend-mix') {
        crumbs.push({ label: 'Economics', to: '/dashboard/assets/economics/spend-mix' });
        crumbs.push({ label: parts[3] ? `Spend mix · ${parts[3]}` : 'Spend mix', to: null });
        return crumbs;
      }
      crumbs.push({ label: 'Economics', to: null });
      return crumbs;
    }
    crumbs.push({ label: 'Assets', to: null });
    return crumbs;
  }
  if (head === 'users') {
    crumbs.push({ label: 'Users', to: null });
    return crumbs;
  }
  if (head === 'products') {
    if (parts[1] === 'email' && parts[3] === 'mapping') {
      crumbs.push({ label: 'Product Management', to: '/dashboard/products' });
      crumbs.push({ label: opts.mailboxAddress || 'Client email mapping', to: null });
      return crumbs;
    }
    crumbs.push({ label: 'Product Management', to: null });
    return crumbs;
  }
  if (head === 'automation') {
    crumbs.push({ label: 'Automation', to: null });
    return crumbs;
  }
  if (head === 'sla') {
    crumbs.push({ label: 'SLA Management', to: null });
    return crumbs;
  }
  if (head === 'form-builder') {
    crumbs.push({ label: 'Form Builder', to: null });
    return crumbs;
  }
  if (head === 'data-sources') {
    crumbs.push({ label: 'Data Sources', to: null });
    return crumbs;
  }
  if (head === 'audit') {
    crumbs.push({ label: 'Audit Log', to: null });
    return crumbs;
  }
  if (head === 'profile') {
    crumbs.push({ label: 'My Profile', to: null });
    return crumbs;
  }
  if (head === 'test-executions') {
    crumbs.push({ label: 'Test Executions', to: null });
    return crumbs;
  }
  if (head === 'admin') {
    crumbs.push({ label: 'Admin', to: null });
    const adminLabels = {
      lookups: 'Manage Dropdowns',
      settings: 'Settings',
      'module-access': 'Module Access',
      'access-control': 'Access Control',
      announcements: 'Announcements',
    };
    if (parts[1]) {
      crumbs[crumbs.length - 1] = { label: 'Admin', to: '/dashboard' };
      crumbs.push({ label: adminLabels[parts[1]] || parts[1], to: null });
    }
    return crumbs;
  }

  crumbs.push({ label: head, to: null });
  return crumbs;
}
